/** Read-only embed cards for Agent Review (no create-task actions). */

import clsx from "clsx";
import { parseChatEmbeds, type ChatEmbed } from "@/components/chat/embeds/types";

interface ChatEmbedEvidenceProps {
  embeds: unknown;
  className?: string;
}

function EmbedCard({ embed }: { embed: ChatEmbed }) {
  if (embed.kind === "task_proposal") {
    const created = Boolean(embed.created_task_id);
    return (
      <div
        className="rounded-lg bg-sky-500/10 px-3 py-2 ring-1 ring-sky-400/25"
        data-testid="agent-review-embed-task-proposal"
      >
        <p className="text-[11px] font-medium uppercase tracking-wide text-sky-300/90">
          Task proposal{created ? " · created" : " · awaiting create"}
        </p>
        <p className="text-sm text-slate-100 mt-0.5">{embed.label}</p>
        <p className="text-xs text-slate-400 mt-0.5">Catalogue: {embed.catalogue_id}</p>
        {embed.why?.trim() ? (
          <p className="text-xs text-slate-400 mt-1 leading-relaxed">{embed.why}</p>
        ) : null}
      </div>
    );
  }

  if (embed.kind === "entity_search_results") {
    return (
      <div
        className="rounded-lg bg-emerald-500/10 px-3 py-2 ring-1 ring-emerald-400/25"
        data-testid="agent-review-embed-entity-search"
      >
        <p className="text-[11px] font-medium uppercase tracking-wide text-emerald-300/90">
          Search · {embed.resource_label}
        </p>
        <p className="text-sm text-slate-100 mt-0.5">
          {embed.total_count} match{embed.total_count === 1 ? "" : "es"}
        </p>
      </div>
    );
  }

  if (embed.kind === "entity_preview") {
    return (
      <div
        className="rounded-lg bg-emerald-500/10 px-3 py-2 ring-1 ring-emerald-400/25"
        data-testid="agent-review-embed-entity-preview"
      >
        <p className="text-[11px] font-medium uppercase tracking-wide text-emerald-300/90">
          Preview · {embed.entity_type}
        </p>
        <p className="text-sm text-slate-100 mt-0.5">{embed.label}</p>
        {embed.subtitle?.trim() ? (
          <p className="text-xs text-slate-400 mt-0.5">{embed.subtitle}</p>
        ) : null}
      </div>
    );
  }

  if (embed.kind === "link") {
    return (
      <div
        className="rounded-lg bg-violet-500/10 px-3 py-2 ring-1 ring-violet-400/25"
        data-testid="agent-review-embed-link"
      >
        <p className="text-[11px] font-medium uppercase tracking-wide text-violet-300/90">
          Link · {embed.target}
        </p>
        <p className="text-sm text-slate-100 mt-0.5">{embed.label}</p>
      </div>
    );
  }

  return null;
}

export function ChatEmbedEvidence({ embeds, className }: ChatEmbedEvidenceProps) {
  const parsed = parseChatEmbeds(embeds);
  if (!parsed.length) return null;

  return (
    <div className={clsx("mt-2 space-y-2", className)} data-testid="agent-review-chat-embeds">
      {parsed.map((embed, index) => (
        <EmbedCard key={`${embed.kind}-${index}`} embed={embed} />
      ))}
    </div>
  );
}
